import { useState, useMemo, useEffect } from "react";
import { useProjects } from "@/hooks/useProjects";
import { useUsers } from "@/hooks/useUsers";
import { ProjectStatus, ProjectType } from "@/types";
import type { Project, ProjectWithPlots, PlotWithStats, HeadsectionStats } from "@/types";
import { ProjectInitialisationDialog } from "@/components/projects/ProjectInitialisationDialog";
import { ProjectDetailDialog } from "@/components/projects/ProjectSpaceDialog";
import { PlotStatsRow } from "@/components/projects/PlotStatsRow";
import { HeadsectionTasksDialog } from "@/components/projects/HeadsectionTasksDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  SearchIcon,
  AddIcon,
  DeleteIcon,
  SpinnerIcon,
  ChevronRightIcon,
} from "@/lib/icons";
import { useSchedulingSyncStatus } from "@/hooks/useSchedulingSync";
import { SchedulingChangesDot } from "@/components/common/SchedulingChangesDot";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability, ProjectCapability } from "@/lib/projectCapabilities";

function SchedulingChangesIndicator({
  projectId,
  schedulingProjectId,
}: {
  projectId: string;
  schedulingProjectId: string | null | undefined;
}) {
  const hasLink = !!schedulingProjectId;
  const { changesDetectedAt } = useSchedulingSyncStatus(
    hasLink ? projectId : null,
  );
  if (!hasLink || !changesDetectedAt) return null;
  return (
    <div className="flex items-center gap-2">
      <SchedulingChangesDot />
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        Muutoksia saatavilla
      </span>
    </div>
  );
}

const STATUS_LABELS: Record<string, string> = {
  [ProjectStatus.TENDERING]: "Tarjousvaihe",
  [ProjectStatus.LOST]: "Hävitty",
  [ProjectStatus.PLANNING]: "Suunnittelu",
  [ProjectStatus.IN_PROGRESS]: "Käynnissä",
  [ProjectStatus.COMPLETED]: "Luovutettu",
  [ProjectStatus.WARRANTY]: "Takuuvaihe",
  [ProjectStatus.ARCHIVED]: "Arkistoitu",
};

const STATUS_COLORS: Record<string, string> = {
  [ProjectStatus.TENDERING]: "bg-violet-50 text-violet-700",
  [ProjectStatus.LOST]: "bg-rose-50 text-rose-700",
  [ProjectStatus.PLANNING]: "bg-slate-100 text-slate-600",
  [ProjectStatus.IN_PROGRESS]: "bg-emerald-50 text-emerald-700",
  [ProjectStatus.COMPLETED]: "bg-sky-50 text-sky-700",
  [ProjectStatus.WARRANTY]: "bg-amber-50 text-amber-700",
  [ProjectStatus.ARCHIVED]: "bg-zinc-100 text-zinc-600",
};

const PROJECT_TYPE_LABELS: Record<string, string> = {
  [ProjectType.KVR]: "KVR-hanke",
  [ProjectType.KOKONAISURAKKA]: "Kokonaisurakka",
  [ProjectType.PROJEKTIJOHTO]: "Projektijohto",
};

function formatUserName(user?: { firstName?: string | null; lastName?: string | null } | null): string {
  if (!user) return "-";
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || "-";
}

export function Hankkeet() {
  const {
    projects,
    loading,
    updateProject,
    deleteProject,
    refetch,
    getProjectRoles,
    createProjectRole,
    deleteProjectRole: deleteProjectRoleMutation,
  } = useProjects();
  const { users: allUsers } = useUsers();

  // Front-end authorization gates (mirror the backend mutation gates so we
  // never show an affordance that would 403).
  //
  // Create project → CREATE_PROJECT, a GLOBAL capability with an INTERNAL+
  // floor, so it is decided by access level alone. Plain INTERNAL is denied by
  // the backend and must not see the affordance.
  const { dbUser } = useAuth();
  const canCreateProject =
    dbUser?.accessLevel === "ADMIN" || dbUser?.accessLevel === "INTERNAL_PLUS";
  const [search, setSearch] = useState("");

  // Expanded project IDs — all expanded by default
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [expandedInitialized, setExpandedInitialized] = useState(false);

  useEffect(() => {
    if (projects.length > 0 && !expandedInitialized) {
      setExpandedIds(new Set(projects.map((p) => p.id)));
      setExpandedInitialized(true);
    }
  }, [projects, expandedInitialized]);

  // Auto-expand any project added after initial load (e.g. newly created projects).
  useEffect(() => {
    if (!expandedInitialized) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const p of projects) {
        if (!next.has(p.id)) {
          next.add(p.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [projects, expandedInitialized]);

  const toggleExpand = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Delete dialog state
  const [projectToDelete, setProjectToDelete] =
    useState<ProjectWithPlots | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deletedProjectName, setDeletedProjectName] = useState<string>("");

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Project detail dialog state. Only the id is held in state — the project
  // itself is derived from `projects` on every render so that the dialog always
  // reads the refetched row. Snapshotting the entity here would freeze its
  // `version` at open time and make the second consecutive save inside the
  // dialog fail the optimistic-lock check.
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);
  const [detailProjectId, setDetailProjectId] = useState<string | null>(null);
  const detailProject = useMemo<Project | null>(
    () => projects.find((p) => p.id === detailProjectId) ?? null,
    [projects, detailProjectId],
  );

  // Headsection tasks dialog state — opens when a user clicks an individual
  // headsection column inside a plot row.
  const [headsectionDialog, setHeadsectionDialog] = useState<{
    plot: PlotWithStats;
    headsectionNumber: number;
    projectName: string;
  } | null>(null);

  const filteredProjects = useMemo(() => {
    if (!search) return projects;
    const lower = search.toLowerCase();
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(lower) ||
        p.description?.toLowerCase().includes(lower),
    );
  }, [projects, search]);

  const openHeadsectionDialog = (
    plot: PlotWithStats,
    stats: HeadsectionStats,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    // Resolve project name so the dialog can render the project line on
    // each task card the same way the kanban does. `projects` already has
    // every project this user sees, so this is a cheap in-memory lookup.
    const project = projects.find((p) => p.id === plot.projectId);
    setHeadsectionDialog({
      plot,
      headsectionNumber: stats.headsectionNumber,
      projectName: project?.name ?? "",
    });
  };

  const openProjectDetail = (project: ProjectWithPlots) => {
    setDetailProjectId(project.id);
    setProjectDetailOpen(true);
  };

  const openDeleteDialog = (
    project: ProjectWithPlots,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setDeleteError(null);
    setProjectToDelete(project);
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await deleteProject(projectToDelete.id);
      if (result?.success) {
        setDeletedProjectName(projectToDelete.name);
        setProjectToDelete(null);
        await refetch();
        setDeleteSuccess(true);
      } else {
        setDeleteError(result?.message || "Hankkeen poisto epäonnistui.");
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
      setDeleteError("Hankkeen poisto epäonnistui.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-5 w-48 mt-2" />
          </div>
          {/* "Luo uusi hanke" button placeholder */}
          <Skeleton className="h-10 w-36 rounded-md" />
        </div>
        {/* Search input — matches the real input's width constraint */}
        <Skeleton className="h-10 w-full max-w-sm rounded-md" />
        <div className="space-y-2">
          {/* Row skeletons match the real project card height:
              py-3.5 (28) + label (~14) + gap-0.5 (2) + value (~22) ≈ 66px */}
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-[66px] w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-6 py-4 border-b bg-background shrink-0 -mx-6 -mt-6">
        <h1 className="text-xl font-semibold tracking-tight">Hankkeet</h1>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Hae hankkeita..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {canCreateProject && (
          <Button onClick={() => setCreateDialogOpen(true)} size="sm" className="ml-auto gap-1.5">
            <AddIcon className="h-4 w-4" />
            Luo uusi hanke
          </Button>
        )}
      </div>
      <div className="pt-4">

      {/* Project list */}
      {filteredProjects.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/70 py-16 text-center text-sm text-muted-foreground">
          Ei hankkeita
        </div>
      ) : (
        <div>
          <div className="space-y-2.5">
            {filteredProjects.map((project) => {
              const isExpanded = expandedIds.has(project.id);
              const hasPlots = project.plots && project.plots.length > 0;

              return (
                <Card key={project.id} className="overflow-x-auto overflow-y-hidden">
                  {/* Inner wrapper: width = max-content of its children, so the
                      row's hover tint and the plot section's border both span
                      the same width regardless of which is intrinsically wider. */}
                  <div className="flex flex-col min-w-max">
                  <div
                    className="flex items-end pl-4 pr-6 py-3.5 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => openProjectDetail(project)}
                  >
                    <div className="flex items-end gap-x-6 flex-shrink-0">
                      <button
                        type="button"
                        className="flex items-center justify-center h-5 w-5 rounded hover:bg-muted -mr-3"
                        onClick={(e) => toggleExpand(project.id, e)}
                        title={isExpanded ? "Piilota lohkot" : "Näytä lohkot"}
                      >
                        <ChevronRightIcon
                          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                            isExpanded ? "rotate-90" : ""
                          }`}
                        />
                      </button>

                      <div className="flex flex-col min-w-0 max-w-[280px]">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80 truncate">
                          Hanke
                        </span>
                        <span
                          className="font-semibold text-[15px] tracking-tight truncate block"
                          title={project.name}
                        >
                          {project.name}
                        </span>
                      </div>

                      <div className="flex flex-col min-w-0">
                        <span className="text-[10px] px-2.5 font-medium uppercase tracking-wider text-muted-foreground/80 truncate">
                          Hanke
                        </span>
                        <Badge
                          className={`${STATUS_COLORS[project.status]} text-xs px-2 py-0.5`}
                          variant="secondary"
                        >
                          {STATUS_LABELS[project.status] || project.status}
                        </Badge>
                      </div>

                      <div className="flex flex-col  min-w-0 max-w-[180px]">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80 truncate">
                          Tyyppi
                        </span>
                        <span className="text-sm truncate">
                          {PROJECT_TYPE_LABELS[project.projectType] || project.projectType}
                        </span>
                      </div>

                      <div className="flex flex-col  min-w-0 max-w-[180px]">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80 truncate">
                          Aikataulu
                        </span>
                        <span className="text-sm whitespace-nowrap tabular-nums text-muted-foreground">
                          {project.startDate
                            ? new Date(project.startDate).toLocaleDateString("fi")
                            : "-"}
                          {" \u2013 "}
                          {project.objectiveEndDate
                            ? new Date(project.objectiveEndDate).toLocaleDateString("fi")
                            : "-"}
                        </span>
                      </div>

                      <div className="flex flex-col gap-0.5 min-w-0 max-w-[180px]">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80 truncate">
                          Työpäällikkö
                        </span>
                        <span className="text-sm truncate">
                          {formatUserName(project.tyopaallikko)}
                        </span>
                      </div>

                      <div className="flex flex-col gap-0.5 min-w-0 max-w-[200px]">
                        <span
                          className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/80 truncate"
                          title="Vastaava työnjohtaja"
                        >
                          Vastaava työnjohtaja
                        </span>
                        <span className="text-sm truncate">
                          {formatUserName(project.vastaavaTyonjohtaja)}
                        </span>
                      </div>
                    </div>

                    {/* Fixed gap — keeps the action cluster sitting just to
                        the right of the metadata grid instead of stretching
                        out to the row's right edge. */}
                    <div className="w-6 flex-shrink-0" />

                    {/* Vertical divider separates the metadata grid from the
                        action region so sync timestamps/buttons read as a
                        distinct cluster instead of two more columns. */}
                    <div className="h-8 w-px bg-border/70 flex-shrink-0 mr-3" aria-hidden />

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <SchedulingChangesIndicator
                        projectId={project.id}
                        schedulingProjectId={
                          project.externalSchedulingPlatformProjectId ?? null
                        }
                      />
                      {/* Deleting a project needs DELETE_PROJECT, which only
                          Työpäällikkö carries — not every project role. */}
                      {hasCapability(project, ProjectCapability.DELETE_PROJECT) && (
                        <div className="flex items-center gap-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => openDeleteDialog(project, e)}
                            title="Poista hanke"
                            className="h-9 px-2.5 text-muted-foreground hover:text-rose-600 hover:bg-rose-50/60"
                          >
                            <DeleteIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded plot rows — indented so the plot name text lands
                      at 60px (same x as the project name). 48 here + PlotStatsRow's
                      own p-3 (12) = 60. */}
                  {isExpanded && (
                    <div className="border-t border-border/60 pr-4 py-3 pl-[48px]">
                      {hasPlots ? (
                        <div className="space-y-2">
                          {project.plots!.map((plot) => (
                            <PlotStatsRow
                              key={plot.id}
                              plot={plot}
                              onHeadsectionClick={openHeadsectionDialog}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Ei lohkoja
                        </p>
                      )}
                    </div>
                  )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Headsection Tasks Dialog */}
      <HeadsectionTasksDialog
        plot={headsectionDialog?.plot ?? null}
        headsectionNumber={headsectionDialog?.headsectionNumber ?? null}
        projectName={headsectionDialog?.projectName ?? null}
        open={!!headsectionDialog}
        onOpenChange={(open) => {
          if (!open) setHeadsectionDialog(null);
        }}
      />

      {/* Project Detail Dialog */}
      <ProjectDetailDialog
        project={detailProject}
        open={projectDetailOpen}
        onOpenChange={setProjectDetailOpen}
        users={allUsers}
        onUpdateProject={async (input) => {
          await updateProject(input);
          await refetch();
        }}
        getProjectRoles={getProjectRoles}
        createProjectRole={createProjectRole}
        deleteProjectRole={deleteProjectRoleMutation}
        onSaved={refetch}
      />

      {/* Create Project Dialog */}
      <ProjectInitialisationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={refetch}
      />

      {/* Delete Project Confirmation Dialog */}
      <AlertDialog
        open={!!projectToDelete}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setProjectToDelete(null);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista hanke</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko todella poistaa hankkeen{" "}
              <strong>"{projectToDelete?.name}"</strong> kaiken datan?
              <br />
              <br />
              Tämä poistaa kaikki hankkeeseen liittyvät lohkot,
              hankintapaketit, työvaiheet, tehtävät, sijainnit ja kommentit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <div className="rounded-md bg-rose-50/80 border border-rose-100 p-3 text-sm text-rose-700">
              {deleteError}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              Peruuta
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? (
                <>
                  <SpinnerIcon className="h-4 w-4 mr-2 animate-spin" />
                  Poistetaan...
                </>
              ) : (
                "Poista"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Success Dialog */}
      <AlertDialog
        open={deleteSuccess}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteSuccess(false);
            setDeletedProjectName("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poisto onnistui</AlertDialogTitle>
            <AlertDialogDescription>
              Hanke <strong>"{deletedProjectName}"</strong> poistettiin
              onnistuneesti. Kaikki hankkeeseen liittyvä data on poistettu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setDeleteSuccess(false);
                setDeletedProjectName("");
              }}
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
